const TERMINAL_EVENT_TYPES = new Set(["done", "error", "terminated"]);
const NON_VERBOSE_EVENT_TYPES = new Set([
  "token",
  "done",
  "error",
  "terminated",
  "follow_up_questions",
  "session_title",
  "ping",
  "clarification_required",
  "waiting_for_clarification",
]);

export type LlmCallMessage = {
  role: string;
  content: string;
};

export type TurnStreamEvent = {
  type: string;
  content?: string;
  label?: string;
  message?: string;
  delta?: string;
  step_title?: string;
  summary?: string;
  insights?: string[];
  why?: string;
  action?: string;
  business_impact?: string;
  detail?: string;
  questions?: string[];
  request?: Record<string, unknown>;
  status?: string;
  turn_id?: string;
  messages?: LlmCallMessage[];
  raw_response?: string;
  input?: Record<string, unknown>;
  output?: Record<string, unknown>;
};

export type PipelineVerboseStep = {
  stepIndex: number;
  eventType: string;
  stepTitle: string;
  summary: string;
  detail: string;
  messages?: LlmCallMessage[];
  rawResponse?: string;
  input?: Record<string, unknown>;
  output?: Record<string, unknown>;
};

export type ParsedTurnStreamEvent = TurnStreamEvent & {
  verboseStep: PipelineVerboseStep | null;
};

export function parseVerboseStep(
  event: TurnStreamEvent,
  stepIndex: number,
): PipelineVerboseStep | null {
  if (NON_VERBOSE_EVENT_TYPES.has(event.type)) {
    return null;
  }

  const stepTitle = event.step_title?.trim();
  const summary = event.summary?.trim();
  const detail = event.detail?.trim();
  const legacyContent = event.content ?? event.label ?? event.message;

  if (stepTitle && summary && detail) {
    return {
      stepIndex,
      eventType: event.type,
      stepTitle,
      summary,
      detail,
      messages: event.messages,
      rawResponse: event.raw_response,
      input: event.input,
      output: event.output,
    };
  }

  if (typeof legacyContent === "string" && legacyContent.trim().length > 0) {
    const text = legacyContent.trim();
    return {
      stepIndex,
      eventType: event.type,
      stepTitle: text,
      summary: text,
      detail: text,
    };
  }

  return null;
}

export function parseTurnStreamEvent(
  rawData: string,
  eventName?: string,
  stepIndex = 0,
): ParsedTurnStreamEvent | null {
  const trimmed = rawData.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const payload = JSON.parse(trimmed) as Record<string, unknown>;
    const type =
      typeof payload.type === "string"
        ? payload.type
        : typeof eventName === "string" && eventName.length > 0
          ? eventName
          : "message";

    const event: TurnStreamEvent = {
      type,
      content: typeof payload.content === "string" ? payload.content : undefined,
      label: typeof payload.label === "string" ? payload.label : undefined,
      message: typeof payload.message === "string" ? payload.message : undefined,
      delta: typeof payload.delta === "string" ? payload.delta : undefined,
      step_title: typeof payload.step_title === "string" ? payload.step_title : undefined,
      summary: typeof payload.summary === "string" ? payload.summary : undefined,
      insights: Array.isArray(payload.insights)
        ? payload.insights.filter((item): item is string => typeof item === "string")
        : undefined,
      why: typeof payload.why === "string" ? payload.why : undefined,
      action: typeof payload.action === "string" ? payload.action : undefined,
      business_impact: typeof payload.business_impact === "string" ? payload.business_impact : undefined,
      detail: typeof payload.detail === "string" ? payload.detail : undefined,
      questions: Array.isArray(payload.questions)
        ? payload.questions.filter((q): q is string => typeof q === "string")
        : undefined,
      request:
        payload.request && typeof payload.request === "object"
          ? (payload.request as Record<string, unknown>)
          : undefined,
      status: typeof payload.status === "string" ? payload.status : undefined,
      turn_id: typeof payload.turn_id === "string" ? payload.turn_id : undefined,
      messages: Array.isArray(payload.messages)
        ? payload.messages.filter(
            (item): item is LlmCallMessage =>
              typeof item === "object" &&
              item !== null &&
              typeof (item as LlmCallMessage).role === "string" &&
              typeof (item as LlmCallMessage).content === "string",
          )
        : undefined,
      raw_response: typeof payload.raw_response === "string" ? payload.raw_response : undefined,
      input:
        payload.input && typeof payload.input === "object"
          ? (payload.input as Record<string, unknown>)
          : undefined,
      output:
        payload.output && typeof payload.output === "object"
          ? (payload.output as Record<string, unknown>)
          : undefined,
    };

    return {
      ...event,
      verboseStep: parseVerboseStep(event, stepIndex),
    };
  } catch {
    if (typeof eventName === "string" && TERMINAL_EVENT_TYPES.has(eventName)) {
      return {
        type: eventName,
        verboseStep: null,
      };
    }

    return {
      type: eventName ?? "message",
      content: trimmed,
      verboseStep: null,
    };
  }
}

export function parseSseChunk(chunk: string): {
  events: ParsedTurnStreamEvent[];
  remainder: string;
} {
  const events: ParsedTurnStreamEvent[] = [];
  const normalized = chunk.replace(/\r\n/g, "\n");
  const blocks = normalized.split("\n\n");
  const remainder = blocks.pop() ?? "";

  for (const block of blocks) {
    if (!block.trim()) {
      continue;
    }

    let eventName: string | undefined;
    let stepIndex = 0;
    const dataLines: string[] = [];

    for (const line of block.split("\n")) {
      if (line.startsWith("event:")) {
        eventName = line.slice("event:".length).trim();
        continue;
      }
      if (line.startsWith("id:")) {
        const parsed = Number.parseInt(line.slice("id:".length).trim(), 10);
        if (!Number.isNaN(parsed)) {
          stepIndex = parsed;
        }
        continue;
      }
      if (line.startsWith("data:")) {
        dataLines.push(line.slice("data:".length).trimStart());
      }
    }

    if (dataLines.length === 0) {
      continue;
    }

    const parsed = parseTurnStreamEvent(dataLines.join("\n"), eventName, stepIndex);
    if (parsed) {
      events.push(parsed);
    }
  }

  return { events, remainder };
}

export function shouldUseTabbedResponse(args: {
  text: string;
  verboseLog: PipelineVerboseStep[];
  followUpQuestions: string[];
}): boolean {
  const trimmed = args.text.trim();
  const isSingleLine = !trimmed.includes("\n");
  const isShort = trimmed.length <= 120;
  const hasExtras = args.verboseLog.length > 0 || args.followUpQuestions.length > 0;

  if (isSingleLine && isShort && !hasExtras) {
    return false;
  }

  return true;
}
